import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { DnDArea } from "components/design/UploadFileSection";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useParseBroadcastChannelsMutation } from "store/apis/broadcast";
import { BackendError } from "types/utils/error";
import { BroadcastCsvHeader } from "./BroadcastCsvHeader";
import { BroadcastMessageChannelTable } from "./BroadcastMessageChannelTable";
import { BULK_CHANNELS_EXCEL_TEMPLATE_URL } from "types/contants/sampleExcelSheet";

type BroadcastCsvProps = {
  title: string;
  file: File | null;
  setChannelLength: (length: number) => void;
  setCSVFile: (file: File | null) => void;
};

export const BroadcastCsv = ({
  title,
  file,
  setChannelLength,
  setCSVFile,
}: BroadcastCsvProps) => {
  const query = useQuery();
  const pageNum = Number(query.get("page_num") || "1");
  const [validateCSV, { data, isLoading, isSuccess }] =
    useParseBroadcastChannelsMutation();
  const { alertToast } = useToast();
  const { update: updateQuery } = useUpdateQuery();

  const onDrop = async (files: File[]) => {
    try {
      const file = files[0];
      setCSVFile(file);
      const channels = await validateCSV({
        broadcast_csv: file,
        page_num: pageNum,
      }).unwrap();
      setChannelLength(channels?.total_count);
    } catch (error) {
      setCSVFile(null);
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleDownloadCsvWithErrors = () => {
    const csvContent = data?.csv_content!;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "broadcast_channels_with_errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (page: number) => {
    updateQuery({
      query: "page_num",
      value: page.toString(),
    });
    if (page !== pageNum && file) {
      onDrop([file]);
    }
  };

  return (
    <div className="t-flex-1 t-space-y-4">
      <BroadcastCsvHeader
        exampleTemplateUrl={BULK_CHANNELS_EXCEL_TEMPLATE_URL}
        customPagination={
          data && (
            <PaginationHandler
              page_num={pageNum}
              per_page={data?.page_size || 20}
              total_count={data?.total_count || 0}
              total_pages={data?.total_pages || 1}
              onPageNumChange={handlePageChange}
            />
          )
        }
        title={title}
        onDrop={onDrop}
      />
      <DashboardContainer.Content>
        <div className="t-h-full">
          {isSuccess || isLoading ? (
            <Async.Root
              {...{ isLoading, isSuccess, isEmpty: data?.total_count === 0 }}
              renderOnLoad={
                <Card.Body className="t-space-y-3">
                  <Skeleton className="t-min-h-10" />
                  <Skeleton className="t-min-h-10" />
                  <Skeleton className="t-min-h-10" />
                </Card.Body>
              }
            >
              <Async.Empty>
                <div className="t-flex t-justify-center t-items-center t-h-full t-flex-col t-gap-2">
                  <p className="t-text-subtext t-text-text-100 t-m-0">
                    No channels found
                  </p>
                  <p className="t-text-body-sm t-text-text-30 t-m-0">
                    Please upload another file to see more channels.
                  </p>
                  {data?.csv_content && (
                    <Button size="small" onClick={handleDownloadCsvWithErrors}>
                      Download CSV with errors
                    </Button>
                  )}
                </div>
              </Async.Empty>
              <Async.Success>
                <BroadcastMessageChannelTable channels={data?.channels || []} />
              </Async.Success>
            </Async.Root>
          ) : (
            <DnDArea
              multiple={false}
              onDrop={onDrop}
              accept={{ "text/csv": [".csv"] }}
              title="Click or drag and drop to upload the filled template"
              subText="File Format: csv"
            />
          )}
        </div>
      </DashboardContainer.Content>
    </div>
  );
};
